import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react'

export type ButtonVariant =
  | 'primary'
  | 'accent'
  | 'secondary'
  | 'outline'
  | 'danger'
  | 'danger-soft'
  | 'ghost'
  | 'link'

export type ButtonSize = 'sm' | 'md' | 'lg'

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant
  size?: ButtonSize
  block?: boolean
  /** Renders a spinner and disables the button; keeps the label so the width does not jump. */
  loading?: boolean
  iconStart?: ReactNode
  iconEnd?: ReactNode
}

function Spinner() {
  return (
    <svg className="btn__spinner" width="16" height="16" viewBox="0 0 16 16" aria-hidden="true">
      <circle cx="8" cy="8" r="6.5" fill="none" stroke="currentColor" strokeOpacity="0.3" strokeWidth="2" />
      <path d="M14.5 8a6.5 6.5 0 0 0-6.5-6.5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  )
}

/**
 * The one button in the product.
 *
 * `type` defaults to `button`: a bare `<button>` inside a form submits it, which has repeatedly
 * been the cause of "the row action reloaded the page" bugs. Submitting is opted into.
 */
const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    variant = 'primary',
    size = 'md',
    block = false,
    loading = false,
    iconStart,
    iconEnd,
    className = '',
    disabled,
    children,
    type = 'button',
    ...rest
  },
  ref,
) {
  const classes = [
    'btn',
    `btn--${variant}`,
    variant === 'link' ? '' : `btn--${size}`,
    block ? 'btn--block' : '',
    className,
  ]
    .filter(Boolean)
    .join(' ')

  return (
    <button ref={ref} type={type} className={classes} disabled={disabled || loading} {...rest}>
      {loading ? <Spinner /> : iconStart}
      {children}
      {!loading && iconEnd}
    </button>
  )
})

export default Button
